import React, { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { clearErrors } from "../../redux/actions/productActions";
import { toast } from "react-toastify";
import useNavHighlight from "../../hooks/useNavHighlight";
import usePageMeta from "../../hooks/usePageMeta";
import Loader from "../loader/loader";
import { createMappingAction, importBarcodeMappingBulkAction } from "../../redux/actions/upcScanActions";
import { MAPPING_IMPORT_RESET } from "../../redux/constants/upcScanConstants";
import MappingList from "./MappingList";
import api from "../../api";
import normalizeUpc, { getNormalizeUpcError } from "../../utils/normalizeUpc";

const BarcodeMapping = () => {


    const dispatch = useDispatch();

    const { loading, error, message } = useSelector(state => state.mapping);
    const { error: importError, inserted, skipped } = useSelector(state => state.mappingImport);

    const [parent_barcode, setParent_barcode] = useState("");
    const [child_barcode, setChild_barcode] = useState("");
    const [unit_case, setUnit_Case] = useState("");
    const [description, setDescription] = useState("");
    const [taxable, setTaxable] = useState(false);

    // Bulk import state
    const [bulkRawRows, setBulkRawRows] = useState([]);
    const [bulkHeaders, setBulkHeaders] = useState([]);
    const [bulkRows, setBulkRows] = useState([]);
    const [bulkColMap, setBulkColMap] = useState({});
    const [bulkHasHeader, setBulkHasHeader] = useState(true);
    const [bulkProgress, setBulkProgress] = useState(0);
    const [bulkShowConfirm, setBulkShowConfirm] = useState(false);
    const [bulkIsImporting, setBulkIsImporting] = useState(false);
    const [exporting, setExporting] = useState(false);



    useNavHighlight("addBarcodeMapping");
    usePageMeta("barcodeMapping");

    const handleExportExcel = async () => {
        setExporting(true);
        try {
            const response = await api.get("/api/v1/mapping/export/excel", {
                responseType: "blob",
            });
            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `master_price_book_${new Date().toISOString().slice(0, 10)}.xlsx`;
            link.click();
            window.URL.revokeObjectURL(url);
            toast.success("Master price book exported");
        } catch (err) {
            toast.error(err?.response?.data?.message || "Failed to export Excel");
        } finally {
            setExporting(false);
        }
    };

    const handleSubmit = (e) => {

        e.preventDefault();

        if (!parent_barcode) {
            toast.error("Please enter case barcode");
            return;
        }

        if (!child_barcode) {
            toast.error("Please enter item barcode");
            return;
        }

        if (!unit_case) {
            toast.error("Please enter units per case");
            return;
        }
        if (Number(unit_case) < 1) {
            toast.error("Please enter valid units per case");
            return;
        }

        // ✅ Validate Parent Barcode
        const parentValidation = validateBarcode(parent_barcode);

        if (!parentValidation.success) {
            toast.error(parentValidation.message);
            return;
        }

        // ✅ Validate Child Barcode
        const childValidation = validateBarcode(child_barcode);

        if (!childValidation.success) {
            toast.error(childValidation.message);
            return;
        }

        const data = {
            parent_barcode: parentValidation.barcode,
            child_barcode: childValidation.barcode,
            unit_case,
            description,
                taxable
            };

        dispatch(createMappingAction(data));
    };


    useEffect(() => {


        if (error) {
            toast.error(error)
            dispatch(clearErrors())
        }
        if (message) {
            toast.success(message)
            dispatch({ type: "CREATE_MAPPING_RESET" })
            
            handleReset();
        }


    }, [error, message, dispatch])


    const handleReset = () => {
        setParent_barcode("");
        setChild_barcode("");
        setUnit_Case("");
        setDescription("");
        setTaxable(false);
    };

    // =====================
    // BULK IMPORT HANDLERS
    // =====================

    const handleBulkFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (evt) => {
            const workbook = XLSX.read(evt.target.result, { type: "binary" });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            setBulkRawRows(data);
            setBulkColMap({});
        };
        reader.readAsBinaryString(file);
    };

    useEffect(() => {
        if (!bulkRawRows.length) return;
        if (bulkHasHeader) {
            setBulkHeaders(bulkRawRows[0]);
            setBulkRows(bulkRawRows.slice(1, 51));
        } else {
            setBulkHeaders(bulkRawRows[0].map((_, i) => `Column ${i + 1}`));
            setBulkRows(bulkRawRows.slice(0, 50));
        }
    }, [bulkRawRows, bulkHasHeader]);

    const buildMappings = () => {
        const result = [];
        const dataRows = bulkHasHeader ? bulkRawRows.slice(1) : bulkRawRows;

        dataRows.forEach(row => {
            const obj = {};
            Object.entries(bulkColMap).forEach(([colIdx, field]) => {
                obj[field] = row[Number(colIdx)];
            });

            if (!obj.parent_barcode || !obj.child_barcode || !obj.unit_case) return;

            const parentVal = validateBarcode(String(obj.parent_barcode));
            const childVal = validateBarcode(String(obj.child_barcode));

            if (!parentVal.success || !childVal.success) return;

            result.push({
                parent_barcode: parentVal.barcode,
                child_barcode: childVal.barcode,
                unit_case: obj.unit_case,
                description: obj.description || "",
                taxable: obj.taxable ? 1 : 0
            });
        });

        return result;
    };

    const handleBulkImport = () => {
        if (bulkIsImporting) return;
        const mappingsToImport = buildMappings();
        if (!mappingsToImport.length) {
            toast.error("No valid mappings found. Check your column mapping and data.");
            setBulkShowConfirm(false);
            return;
        }
        setBulkIsImporting(true);
        dispatch(importBarcodeMappingBulkAction(mappingsToImport, setBulkProgress));
    };

    useEffect(() => {
        if (importError) {
            toast.error(importError);
            dispatch({ type: MAPPING_IMPORT_RESET });
            setBulkShowConfirm(false);
            setBulkIsImporting(false);
        }
    }, [importError, dispatch]);

    const bulkUsedFields = Object.values(bulkColMap);
    const BULK_FIELDS = ["parent_barcode", "child_barcode", "unit_case", "description", "taxable"];
    const REQUIRED_BULK_FIELDS = ["parent_barcode", "child_barcode", "unit_case"];
    const unmappedBulkFields = REQUIRED_BULK_FIELDS.filter(f => !bulkUsedFields.includes(f));



const validateBarcode = (barcode) => {
    if (!barcode) {
        return { success: false, message: "Barcode is required" };
    }

    const raw = String(barcode).replace(/\s/g, "");

    if (!/^\d+$/.test(raw)) {
        return { success: false, message: "Invalid barcode. Only numbers are allowed." };
    }

    const normalized = normalizeUpc(raw);
    if (!normalized) {
        return { success: false, message: getNormalizeUpcError(raw) };
    }

    return { success: true, barcode: normalized };
};


    return (
        <Fragment>
            <section className="pl-[1.5vmax] pr-[2.5vmax] ]">
                {loading && <Loader />}
                <div className="flex justify-between items-center bg-white p-[1.2vmax] rounded-[1vmax] px-[2vmax]">
                    <h2 className="text-[1.3vmax] font-bold">Master Price Book</h2>

                    <div className="flex-center gap-[1vmax] text-[1vmax]">
                        <button
                            type="button"
                            onClick={handleExportExcel}
                            disabled={exporting}
                            className="border border-green-600 text-green-700 text-[0.95vmax] py-[0.5vmax] px-[1vmax] rounded-lg flex items-center gap-[0.4vmax] hover:bg-green-50 disabled:opacity-50"
                        >
                            <i className="fa-solid fa-file-excel" />
                            {exporting ? "Exporting..." : "Export All Excel"}
                        </button>
                        <Link to={"/admin"}>Dashboard</Link>
                        <div className="w-[0.3vmax] h-[0.3vmax] rounded-[50%] bg-black"></div>
                        <span className="text-slate-400">Master Price Book</span>
                    </div>
                </div>
            </section>


            <section className="pl-[1.5vmax] pr-[2.5vmax] mt-[1vmax] mb-[1vmax]">
                <div className="p-[2vmax] bg-white shadow-1 rounded-2xl">
                    <form onSubmit={handleSubmit} >
                        <h2 className="text-[1.5vmax] font-2 font-bold py-[2vmax]">Add New Entry</h2>
                        <div className="flex justify-start gap-[1.5vmax] items-center flex-wrap">
                            <div className="w-[22%] flex flex-col gap-[0.5vmax]">
                                <span className="text-[1vmax] font-[600] text-gray-600">Case Barcode &nbsp;<span className="text-red-600">*</span></span>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    className="p-[0.8vmax_1vmax] border-[0.15vmax] border-slate-300 rounded-xl outline-[var(--color-2)]"
                                    placeholder="Case Barcode"
                                    value={parent_barcode}
                                    onChange={(e) => setParent_barcode(e.target.value.replace(/\D/g, ""))}
                                    onBlur={() => {
                                        const result = validateBarcode(parent_barcode);
                                        if (result.success) setParent_barcode(result.barcode);
                                    }}
                                />
                            </div>
                            <div className="w-[22%] flex flex-col gap-[0.5vmax]">
                                <span className="text-[1vmax] font-[600] text-gray-600">Units per Case &nbsp;<span className="text-red-600">*</span></span>
                                <input type="number" className="p-[0.8vmax_1vmax] border-[0.15vmax] border-slate-300 rounded-xl outline-[var(--color-2)]" placeholder="Unit / Case"
                                    value={unit_case} onChange={(e) => setUnit_Case(e.target.value)} min="1" />
                            </div>
                            <div className="w-[22%] flex flex-col gap-[0.5vmax]">
                                <span className="text-[1vmax] font-[600] text-gray-600">Item Barcode &nbsp;<span className="text-red-600">*</span></span>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="off"
                                    className="p-[0.8vmax_1vmax] border-[0.15vmax] border-slate-300 rounded-xl outline-[var(--color-2)]"
                                    placeholder="Item Barcode"
                                    value={child_barcode}
                                    onChange={(e) => setChild_barcode(e.target.value.replace(/\D/g, ""))}
                                    onBlur={() => {
                                        const result = validateBarcode(child_barcode);
                                        if (result.success) setChild_barcode(result.barcode);
                                    }}
                                />
                            </div>
                            <div className="w-[22%] flex flex-col gap-[0.5vmax]">
                                <span className="text-[1vmax] font-[600] text-gray-600">Description</span>
                                <input type="text" className="p-[0.8vmax_1vmax] border-[0.15vmax] border-slate-300 rounded-xl outline-[var(--color-2)]" placeholder="Product Description"
                                    value={description} onChange={(e) => setDescription(e.target.value)} />
                            </div>
                        </div>
                        <div className="flex gap-[2vmax] mt-[1.5vmax] items-center">
                            <label className="flex items-center gap-[0.5vmax] text-[1vmax] cursor-pointer">
                                <input type="checkbox" checked={taxable} onChange={(e) => setTaxable(e.target.checked)} />
                                <span className="font-[600] text-gray-600">Taxable</span>
                            </label>
                        </div>



                        <div className="flex justify-between gap-[1vmax] mt-10">
                            <span className="font-[500] text-gray-600 " >
                                <i class="fa-solid fa-circle-info"></i>
                                &nbsp;&nbsp;&nbsp;&nbsp;Fields with "<span className="text-red-600" > * </span>" are required</span>
                            <div className="flex gap-[1.5vmax]">
                                <button className="text-[1.1vmax] bg-gray-500 hover:bg-gray-700 text-white px-[1vmax] py-[0.5vmax] rounded-lg" type="button" onClick={handleReset} >Reset</button>
                                <button className="text-[1.1vmax] bg-green-600 hover:bg-green-700 text-white px-[1vmax] py-[0.5vmax] rounded-lg" type="submit" >Save </button>
                            </div>
                        </div>
                    </form>
                </div>
            </section>
            {/* Bulk Import Section */}
            <section className="pl-[1.5vmax] pr-[2.5vmax] mt-[1vmax] mb-[1vmax]">
                <div className="p-[2vmax] bg-white shadow-1 rounded-2xl">
                    <h2 className="text-[1.5vmax] font-2 font-bold mb-[1.5vmax]">Bulk Import Master Price Book</h2>

                    <div className="flex items-center gap-[1.5vmax] mb-[1vmax]">
                        <input
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            onChange={handleBulkFileUpload}
                            className="text-[1vmax]"
                        />
                        <label className="flex items-center gap-[0.5vmax] text-[1vmax]">
                            <input
                                type="checkbox"
                                checked={bulkHasHeader}
                                onChange={() => setBulkHasHeader(p => !p)}
                            />
                            File has header row
                        </label>
                        {bulkRows.length > 0 && (
                            <button
                                type="button"
                                disabled={unmappedBulkFields.length > 0}
                                onClick={() => setBulkShowConfirm(true)}
                                className="bg-green-600 hover:bg-green-700 text-white px-[1.5vmax] py-[0.6vmax] rounded-lg text-[1vmax] disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Import Mappings
                            </button>
                        )}
                    </div>

                    <div className="text-[0.9vmax] text-gray-500 mb-[1vmax]">
                        Required: <strong>parent_barcode</strong>, <strong>child_barcode</strong>, <strong>unit_case</strong> &nbsp;|&nbsp; Optional: <strong>description</strong>, <strong>taxable</strong> (1 = taxable, 0 = not)
                    </div>

                    {bulkRows.length > 0 && (
                        <>
                            {unmappedBulkFields.length > 0 && (
                                <div className="mb-3 p-3 bg-yellow-50 border border-yellow-300 rounded text-sm text-yellow-800">
                                    <strong>Map these required columns first:</strong>{" "}
                                    {unmappedBulkFields.join(", ")}
                                </div>
                            )}
                            <div className="overflow-x-auto">
                                <div className="flex bg-gray-100 min-w-max">
                                    {bulkHeaders.map((h, i) => (
                                        <div key={i} className="w-48 border p-2">
                                            <select
                                                className="w-full mb-1 border rounded text-sm"
                                                value={bulkColMap[i] || ""}
                                                onChange={(e) =>
                                                    setBulkColMap(prev => ({ ...prev, [i]: e.target.value }))
                                                }
                                            >
                                                <option value="">Skip</option>
                                                {BULK_FIELDS.filter(
                                                    f => !bulkUsedFields.includes(f) || bulkColMap[i] === f
                                                ).map(f => (
                                                    <option key={f} value={f}>{f}</option>
                                                ))}
                                            </select>
                                            <div className="text-xs font-semibold text-center">{h}</div>
                                        </div>
                                    ))}
                                </div>

                                {bulkRows.map((row, rIdx) => (
                                    <div key={rIdx} className="flex min-w-max">
                                        {bulkHeaders.map((_, cIdx) => (
                                            <div key={cIdx} className="w-48 border p-2 text-sm">
                                                {row[cIdx] ?? ""}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </section>

            {/* Bulk Import Confirm Popup */}
            {bulkShowConfirm && (
                <div className="fixed w-[100vw] h-[100vh] top-0 left-0 bg-[#000000af] flex-center z-[9999999999]">
                    <div className="bg-white p-[2vmax] rounded-xl">
                        <p>Are you sure you want to import these barcode mappings?<br />Please make sure the data is correct.</p>
                        <br />
                        <div className="flex justify-end gap-[2vmax]">
                            <button className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded" onClick={() => setBulkShowConfirm(false)}>No</button>
                            <button
                                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded disabled:opacity-50"
                                onClick={handleBulkImport}
                                disabled={bulkIsImporting}
                            >
                                {bulkIsImporting ? "Importing..." : "Yes"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bulk Import Progress */}
            {bulkProgress > 0 && (
                <div className="fixed w-[100vw] h-[100vh] top-0 left-0 bg-white flex-center z-[9999999999] flex-col">
                    <div className="mt-4 bg-gray-500 rounded w-[50%]">
                        <div
                            className="bg-green-600 text-white text-center rounded p-[0.2vmax]"
                            style={{ width: `${bulkProgress}%` }}
                        >
                            {bulkProgress}%
                        </div>
                    </div>
                    <div className="mt-[2vmax]">
                        <span className="font-bold">Mappings Imported: &nbsp;</span>
                        <span>{inserted || 0}</span>
                    </div>
                    <div className="pt-[0.5vmax]">
                        <span className="font-bold">Mappings Skipped: &nbsp;</span>
                        <span>{skipped || 0}</span>
                    </div>
                    <div className="mt-[2vmax] w-[40%] text-center text-sm text-gray-500">
                        Rows are skipped if barcodes already exist, are duplicates, or are invalid.
                    </div>
                    {bulkProgress === 100 && (
                        <button
                            className="p-[0.6vmax_1vmax] rounded-2xl bg-[image:var(--gradient-2)] text-white mt-[2vmax]"
                            onClick={() => {
                                dispatch({ type: MAPPING_IMPORT_RESET });
                                setBulkProgress(0);
                                setBulkIsImporting(false);
                                setBulkShowConfirm(false);
                                setBulkRawRows([]);
                                setBulkHeaders([]);
                                setBulkRows([]);
                                setBulkColMap({});
                            }}
                        >
                            Done
                        </button>
                    )}
                </div>
            )}

            <MappingList validateBarcode={validateBarcode} />
        </Fragment>
    );
};

export default BarcodeMapping;
